import React from 'react';
import { usePayrollSettings } from './hooks/usePayrollSettings';
import AppDateInput from '../../../components/AppDateInput';
import AppDropdown from '../../../components/AppDropdown';
import AppToggle from '../../../components/AppToggle';

export default function PayrollSettingsScreen() {
  const { state, actions } = usePayrollSettings();
  const { formData, isLoading, isSaving, isDirty, monthOptions, leaveSettingOptions } = state;

  const formatAuditDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Top Header Canopy */}
      <section className="flex flex-col gap-2 border-b border-outline-variant/10 pb-5">
        <div className="flex items-center gap-2 text-xs font-bold text-on-surface-variant">
          <span>Payroll</span>
          <span className="material-symbols-outlined text-xs">chevron_right</span>
          <span className="text-primary font-black">Settings</span>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
              Payroll Settings
            </h1>
            <p className="mt-1.5 text-xs text-on-surface-variant font-medium max-w-2xl leading-relaxed">
              Configure organizational payroll start dates, annual closing cycle months, and automated leave credit policies.
            </p>
          </div>

          {/* Audit Timestamp Badge */}
          {formData.updatedOn && (
            <div className="flex items-center gap-2 rounded-xl bg-surface-container-high/60 border border-outline-variant/20 px-3.5 py-2 text-xs font-semibold text-on-surface-variant w-fit shadow-2xs">
              <span className="material-symbols-outlined text-sm text-primary">history</span>
              <span>
                Last updated:{' '}
                <strong className="text-on-surface">{formatAuditDate(formData.updatedOn)}</strong>
                {formData.updatedByName && (
                  <>
                    {' '}by <strong className="text-on-surface">{formData.updatedByName}</strong>
                  </>
                )}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Main Settings Card */}
      <div className="rounded-2xl border border-outline-variant/25 bg-surface-container-lowest overflow-hidden shadow-xs">
        {/* Card Header Bar */}
        <div className="flex items-center justify-between border-b border-outline-variant/20 bg-surface-container-low/80 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <span className="material-symbols-outlined text-primary text-xl">tune</span>
            <h2 className="text-sm font-bold text-on-surface">
              Organizational Payroll Configuration
            </h2>
          </div>
          {isDirty && (
            <span className="inline-flex items-center gap-1 rounded-lg bg-amber-500/10 px-2.5 py-1 text-[11px] font-bold text-amber-700 dark:text-amber-400 border border-amber-500/20">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse"></span>
              Unsaved Changes
            </span>
          )}
        </div>

        {/* Card Body */}
        <div className="p-6 sm:p-8">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 text-center text-xs text-on-surface-variant font-semibold">
              <span className="material-symbols-outlined mb-2 text-3xl animate-spin text-primary">
                progress_activity
              </span>
              Loading payroll configurations...
            </div>
          ) : (
            <form onSubmit={actions.handleSave} className="max-w-3xl space-y-7">
              
              {/* Field 1: Payroll start date */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 items-start">
                <div className="sm:col-span-4 pt-2">
                  <label className="text-xs font-bold text-on-surface block">
                    Payroll start date
                  </label>
                  <p className="text-[11px] text-on-surface-variant mt-0.5 leading-snug">
                    Baseline anchor date for leave balances, quota accruals, and service calculations.
                  </p>
                </div>
                <div className="sm:col-span-8 max-w-md">
                  <AppDateInput
                    value={formData.payrollStartDate}
                    onChange={(val) => actions.handleChange('payrollStartDate', val)}
                    placeholder="YYYY-MM-DD"
                    inputClassName="h-10 text-xs font-semibold bg-surface-container-lowest rounded-xl border border-outline-variant/30 focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              {/* Field 2: Payroll year end month */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 items-start">
                <div className="sm:col-span-4 pt-2">
                  <label className="text-xs font-bold text-on-surface block">
                    Payroll year end month
                  </label>
                  <p className="text-[11px] text-on-surface-variant mt-0.5 leading-snug">
                    Fiscal closing month for annual carry-forward caps, leave quota resets, and tax periods.
                  </p>
                </div>
                <div className="sm:col-span-8 max-w-md">
                  <AppDropdown
                    options={monthOptions}
                    value={formData.payrollYearEndMonth}
                    onChange={(val) => actions.handleChange('payrollYearEndMonth', val)}
                    placeholder="Select Year End Month"
                    triggerClassName="h-10 text-xs font-bold bg-surface-container-lowest w-full rounded-xl border border-outline-variant/30 px-3.5 focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              {/* Field 3: Payroll leave setting */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 items-start">
                <div className="sm:col-span-4 pt-2">
                  <label className="text-xs font-bold text-on-surface block">
                    Payroll leave setting
                  </label>
                  <p className="text-[11px] text-on-surface-variant mt-0.5 leading-snug">
                    Determines whether leave quotas accrue incrementally every month or upfront once per year.
                  </p>
                </div>
                <div className="sm:col-span-8 max-w-md">
                  <AppDropdown
                    options={leaveSettingOptions}
                    value={formData.payrollLeaveSetting}
                    onChange={(val) => actions.handleChange('payrollLeaveSetting', val)}
                    placeholder="Select Leave Credit Setting"
                    triggerClassName="h-10 text-xs font-bold bg-surface-container-lowest w-full rounded-xl border border-outline-variant/30 px-3.5 focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              {/* Field 4: Allow leave application > available leaves */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 items-start">
                <div className="sm:col-span-4 pt-1">
                  <label className="text-xs font-bold text-on-surface block">
                    Allow leave application &gt; available leaves
                  </label>
                  <p className="text-[11px] text-on-surface-variant mt-0.5 leading-snug">
                    When enabled, allows employees to apply with zero/insufficient balance, deducted as Loss of Pay (LOP) in salary.
                  </p>
                </div>
                <div className="sm:col-span-8 flex items-center pt-1.5">
                  <AppToggle
                    checked={Boolean(formData.allowLeaveOverAvailable)}
                    onChange={(val) => actions.handleChange('allowLeaveOverAvailable', val)}
                    ariaLabel="Allow leave application greater than available leaves"
                  />
                </div>
              </div>

            </form>
          )}
        </div>

        {/* Card Footer Bar with Save & Discard Actions */}
        <div className="flex items-center justify-end gap-3 border-t border-outline-variant/15 bg-surface-container-low/40 px-6 py-4">
          <button
            type="button"
            onClick={actions.handleDiscard}
            disabled={!isDirty || isSaving || isLoading}
            className="rounded-xl border border-outline-variant/30 bg-surface px-5 py-2.5 text-xs font-bold text-on-surface hover:bg-surface-container-high transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            Discard Changes
          </button>

          <button
            type="button"
            onClick={actions.handleSave}
            disabled={isSaving || isLoading}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-2.5 text-xs font-bold text-on-primary shadow-sm transition-all hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
          >
            {isSaving ? (
              <>
                <span className="material-symbols-outlined text-sm animate-spin">
                  progress_activity
                </span>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-sm">save</span>
                <span>Save Settings</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
